/**
 * Organization Paused Page
 *
 * Shown to organization members when their organization is paused.
 * Displays:
 * - Why the organization is paused
 * - Organization owner contact information
 * - Instructions to contact owner
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle, Mail, Phone, User, ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';

interface OrganizationOwner {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  company?: string;
}

interface OrganizationDetails {
  id: string;
  name: string;
  status: string;
  pausedAt?: string;
  pausedReason?: string;
  owner: OrganizationOwner;
}

export default function OrganizationPausedPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<OrganizationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      if (!user?.organizationMembership?.organizationId) {
        navigate('/dashboard');
        return;
      }

      try {
        const response = await api.get<OrganizationDetails>(
          `/api/organizations/${user.organizationMembership.organizationId}/details`
        );
        setOrganization(response);
      } catch (err: any) {
        console.error('Failed to fetch organization details:', err);
        setError('Unable to load organization information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizationDetails();
  }, [user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const ownerName = organization?.owner
    ? `${organization.owner.firstName || ''} ${organization.owner.lastName || ''}`.trim() || 'Organization Owner'
    : 'Organization Owner';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mb-4">
            <AlertCircle className="h-10 w-10 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Organization Paused
          </h1>
          <p className="text-lg text-gray-600">
            {organization?.name || 'Your organization'} is currently unavailable
          </p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
          {/* Reason Section */}
          {organization?.pausedReason && (
            <div className="bg-amber-50 border-b border-amber-100 px-6 py-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-1">
                Why is this happening?
              </h3>
              <p className="text-sm text-amber-800">
                {organization.pausedReason}
              </p>
            </div>
          )}

          {/* Instructions */}
          <div className="px-6 py-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              What should I do?
            </h3>
            <p className="text-gray-700 mb-4">
              This organization has been temporarily paused. Please contact the organization owner to resolve this issue and restore access.
            </p>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> Only the organization owner can reactivate this organization by upgrading to the Grow plan or resolving any outstanding payment issues.
              </p>
            </div>
          </div>

          {/* Owner Contact Information */}
          <div className="px-6 py-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Contact Organization Owner
            </h3>

            {error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                {error}
              </div>
            ) : organization?.owner ? (
              <div className="space-y-4">
                {/* Owner Name */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <User className="h-5 w-5 text-gray-400 mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Name</p>
                    <p className="text-base text-gray-900">{ownerName}</p>
                  </div>
                </div>

                {/* Company */}
                {organization.owner.company && (
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-5 w-5 text-gray-400 mt-0.5 flex items-center justify-center">
                        🏢
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Company</p>
                      <p className="text-base text-gray-900">{organization.owner.company}</p>
                    </div>
                  </div>
                )}

                {/* Email */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <a
                      href={`mailto:${organization.owner.email}`}
                      className="text-base text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {organization.owner.email}
                    </a>
                  </div>
                </div>

                {/* Phone */}
                {organization.owner.phone && (
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Phone</p>
                      <a
                        href={`tel:${organization.owner.phone}`}
                        className="text-base text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        {organization.owner.phone}
                      </a>
                    </div>
                  </div>
                )}

                {/* Contact Button */}
                <div className="pt-2">
                  <a
                    href={`mailto:${organization.owner.email}?subject=Organization%20Paused%20-%20${encodeURIComponent(organization.name)}`}
                    className="inline-flex items-center justify-center w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email to Owner
                  </a>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Owner contact information unavailable</p>
            )}
          </div>

          {/* Paused Date */}
          {organization?.pausedAt && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Paused on {new Date(organization.pausedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleLogout}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Sign Out
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Need more help?{' '}
            <a href="/contact" className="text-blue-600 hover:text-blue-700 hover:underline">
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
